import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const HIGGINS_PURPLE = '#8b189b'; // Higgins brand purple (PMS 2602C)

/** Links that leave the help (http/https) open in a new tab; in-page anchors and relative links stay put. */
const EXTERNAL_LINK = /^https?:\/\//i;

/** A trailing `{#anchor}` on a heading sets its id: `## Branch Manager {#bm}` → `<h2 id="bm">`. */
const HEADING_ID = /\s*\{#([A-Za-z0-9_-]+)\}\s*$/;

interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
  data?: { hProperties?: Record<string, unknown> };
}

function setHeadingIds(node: MdNode): void {
  if (node.type === 'heading' && node.children?.length) {
    const last = node.children[node.children.length - 1];
    const match = last.type === 'text' && last.value ? HEADING_ID.exec(last.value) : null;
    if (match && last.value) {
      last.value = last.value.slice(0, match.index);
      node.data = { ...node.data, hProperties: { ...node.data?.hProperties, id: match[1] } };
    }
  }
  node.children?.forEach(setHeadingIds);
}

/** Remark plugin: stable heading anchors from `{#anchor}`, so deep links survive a reworded heading. */
function remarkHeadingIds() {
  return (tree: MdNode) => setHeadingIds(tree);
}

/** Maps markdown elements to Tailwind-styled equivalents — avoids the typography plugin. */
const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  h1: ({ node, ...props }) => (
    <h1 className="mt-6 mb-3 text-xl font-semibold tracking-tight first:mt-0" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="mt-6 mb-2 text-lg font-semibold tracking-tight first:mt-0" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="mt-4 mb-2 text-base font-semibold first:mt-0" {...props} />
  ),
  p: ({ node, ...props }) => <p className="mb-3 text-sm leading-relaxed" {...props} />,
  ul: ({ node, ...props }) => <ul className="mb-3 ml-5 list-disc space-y-1 text-sm" {...props} />,
  ol: ({ node, ...props }) => <ol className="mb-3 ml-5 list-decimal space-y-1 text-sm" {...props} />,
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  a: ({ node, href, ...props }) => (
    <a
      className="font-medium underline underline-offset-2 hover:opacity-80"
      style={{ color: HIGGINS_PURPLE }}
      href={href}
      {...(href && EXTERNAL_LINK.test(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...props}
    />
  ),
  code: ({ node, ...props }) => (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.8em]" {...props} />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="mb-3 border-l-2 pl-3 text-sm italic text-muted-foreground"
      style={{ borderColor: HIGGINS_PURPLE }}
      {...props}
    />
  ),
  table: ({ node, ...props }) => (
    <div className="mb-3 overflow-x-auto">
      <table className="w-full border-collapse text-sm" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => (
    <th className="border-b px-2 py-1.5 text-left font-semibold" {...props} />
  ),
  td: ({ node, ...props }) => <td className="border-b px-2 py-1.5 align-top" {...props} />,
  hr: ({ node, ...props }) => <hr className="my-4 border-border" {...props} />,
};

export interface HelpMarkdownProps {
  /** Raw markdown, e.g. an app's docs/USERS.md imported with `?raw`. */
  source: string;
}

/**
 * The one Markdown renderer for Higgins help — used by `HelpPanel` (in-app help) and the
 * Power BI Help site, so help reads and looks the same everywhere.
 */
export function HelpMarkdown({ source }: HelpMarkdownProps) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm, remarkHeadingIds]} components={markdownComponents}>
      {source}
    </ReactMarkdown>
  );
}
